import { useAppDispatch } from '@core/redux/hooks';
import {
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { TransformationReason } from '@processingResults/components';
import type { ProcessingResult } from '@processingResults/types';
import { hasUserTransformationData } from '@scans/components/Scans/helper';
import {
  getPersistedTransformationMode,
  shouldShowManualPositionWarning,
  type TransformationMode,
} from '@scans/helper';
import { scanActions } from '@scans/redux';
import type { Scan } from '@scans/types';
import { useCallback, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { setManualPositionWarningDialog } from '../ManualPositionWarningDialog';
import {
  setTransformTrenchPointsWithScan,
  setTriggerUndoUseUserTransformation,
  transformTrenchPointsWithScanState,
} from './state';

// ============================================================================
// Types
// ============================================================================

interface ScanTransformationProps {
  scan: Scan;
  selectedProcessingResult?: ProcessingResult;
}

/**
 * Checks if processing result has auto-alignment transformation data.
 */
const hasAutoAlignmentData = (processingResult?: ProcessingResult): boolean => {
  return Boolean(
    processingResult &&
    (processingResult.autoAlignmentRotation !== null ||
      processingResult.autoAlignmentTranslation !== null),
  );
};

/**
 * Determines if "User Transformation" radio button should be visible.
 * Shown when there's user transformation data, even if reason is UNSPECIFIED.
 * Hidden when transformation reason is AUTO_ALIGNED and there's no user transformation data.
 */
const shouldShowUserTransformation = (processingResult?: ProcessingResult): boolean => {
  if (!processingResult) return false;

  // Always show if there's user transformation data
  if (hasUserTransformationData(processingResult)) return true;

  // Hide when reason is AUTO_ALIGNED or UNSPECIFIED (and no user transformation data)
  const reason = processingResult.reason;
  return reason !== TransformationReason.AutoAligned && reason !== TransformationReason.Unspecified;
};

/**
 * Determines the current transformation mode based on scan flags and processing result reason.
 */
const getCurrentTransformationMode = (
  disableUserTransformation?: boolean,
  disableAutoAlignedPosition?: boolean,
  processingResultReason?: TransformationReason,
  processingResult?: ProcessingResult,
): TransformationMode => {
  // If flags are uninitialized, infer mode from persisted reason
  if (disableUserTransformation === undefined && disableAutoAlignedPosition === undefined) {
    if (processingResultReason === TransformationReason.AutoAligned) return 'autoAlign';
    if (processingResultReason === TransformationReason.Unspecified) {
      // Check if there's user transformation data even when reason is UNSPECIFIED
      return hasUserTransformationData(processingResult) ? 'user' : 'none';
    }
    return 'user';
  }

  // Check flags in priority order
  if (!disableUserTransformation) return 'user';
  if (!disableAutoAlignedPosition) return 'autoAlign';

  return 'none'; // Both disabled = original position
};

// ============================================================================
// Component
// ============================================================================

/**
 * ScanTransformation component handles the UI for switching between different
 * scan transformation modes: Original, User Transformation, and Auto Alignment.
 *
 * The component maintains two states:
 * - UI state: Currently selected mode in the radio group (for preview)
 * - Persisted state: Currently saved mode in the backend (marked with "applied" label)
 *
 * Business Rules:
 * - "User Transformation" is hidden when reason is AUTO_ALIGNED or UNSPECIFIED
 * - "Auto Alignment" is only shown if processing result has auto-alignment data
 * - "(applied)" label shows which mode is currently persisted in the backend
 * - "Apply selected" button is only enabled when UI state differs from persisted state
 * - Clicking "Apply selected" saves the UI state to the backend
 */
export const ScanTransformation = ({ scan, selectedProcessingResult }: ScanTransformationProps) => {
  const dispatch = useAppDispatch();
  const { applyTransformation: transformAllPoints } = useSnapshot(
    transformTrenchPointsWithScanState,
  );

  // ============================================================================
  // Sync Redux flags with persisted state on scan selection
  // ============================================================================

  // Reset Redux flags to match the persisted state when a new scan is selected
  // This ensures that stale flags from previous interactions don't cause issues
  useEffect(() => {
    if (!scan?.id) return;
    const persistedMode = getPersistedTransformationMode(selectedProcessingResult);

    const expectedDisableUser = persistedMode !== 'user';
    const expectedDisableAuto = persistedMode !== 'autoAlign';

    // Only update if the flags don't match the persisted state
    if (
      scan.disableUserTransformation !== expectedDisableUser ||
      scan.disableAutoAlignedPosition !== expectedDisableAuto
    ) {
      dispatch(
        scanActions.updateOne({
          id: scan.id,
          disableUserTransformation: expectedDisableUser,
          disableAutoAlignedPosition: expectedDisableAuto,
        }),
      );
    }
  }, [scan?.id, selectedProcessingResult?.id, dispatch]);

  // ============================================================================
  // Computed State
  // ============================================================================

  const hasAutoAlignment = hasAutoAlignmentData(selectedProcessingResult);
  const showUserTransformation = shouldShowUserTransformation(selectedProcessingResult);
  const currentMode = getCurrentTransformationMode(
    scan?.disableUserTransformation,
    scan?.disableAutoAlignedPosition,
    selectedProcessingResult?.reason,
    selectedProcessingResult,
  );
  const persistedMode = getPersistedTransformationMode(selectedProcessingResult);
  const hasUnsavedChanges = currentMode !== persistedMode;

  const handleModeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!scan?.id) return;

      const mode = event.target.value as TransformationMode;

      // Update Redux immediately to preview the transformation in the 3D viewer
      dispatch(
        scanActions.updateOne({
          id: scan.id,
          disableUserTransformation: mode !== 'user',
          disableAutoAlignedPosition: mode !== 'autoAlign',
        }),
      );
    },
    [scan?.id, dispatch],
  );

  const handleTransformAllPointsToggle = useCallback(() => {
    setTransformTrenchPointsWithScan(!transformAllPoints);
  }, [transformAllPoints]);

  const handleApplySelected = useCallback(() => {
    if (!hasUnsavedChanges) return;

    const handleProceed = () => {
      // Trigger the backend persistence of the current transformation mode
      setTriggerUndoUseUserTransformation(true);
    };

    const handleCancel = () => {
      if (scan?.id) {
        dispatch(
          scanActions.updateOne({
            id: scan.id,
            disableUserTransformation: persistedMode !== 'user',
            disableAutoAlignedPosition: persistedMode !== 'autoAlign',
          }),
        );
      }
    };

    if (shouldShowManualPositionWarning(scan, selectedProcessingResult)) {
      setManualPositionWarningDialog(true, handleProceed, handleCancel);
    } else {
      handleProceed();
    }
  }, [hasUnsavedChanges, scan, selectedProcessingResult, persistedMode, dispatch]);

  return (
    <Stack gap={1} padding={2}>
      <Typography variant="overline">Scan Transformation</Typography>

      {/* Transform Points Toggle */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography>Transform all points with scan</Typography>
        <Switch checked={transformAllPoints} onClick={handleTransformAllPointsToggle} />
      </Stack>

      {/* Transformation Mode Selection */}
      <FormControl component="fieldset">
        <RadioGroup value={currentMode} onChange={handleModeChange}>
          {/* Original: No transformation applied */}
          <FormControlLabel
            value="none"
            control={<Radio />}
            label={`Original${persistedMode === 'none' ? ' (applied)' : ''}`}
          />

          {/* User Transformation: Manual user adjustments */}
          {showUserTransformation && (
            <FormControlLabel
              value="user"
              control={<Radio />}
              label={`Manual${persistedMode === 'user' ? ' (applied)' : ''}`}
            />
          )}

          {/* Auto Alignment: System-calculated alignment */}
          {hasAutoAlignment && (
            <FormControlLabel
              value="autoAlign"
              control={<Radio />}
              label={`Auto Alignment${persistedMode === 'autoAlign' ? ' (applied)' : ''}`}
            />
          )}
        </RadioGroup>
      </FormControl>

      {/* Apply Button: Persists the selected mode (only enabled when UI differs from persisted) */}
      <Button
        variant="outlined"
        color="error"
        disabled={!hasUnsavedChanges}
        onClick={handleApplySelected}
      >
        Apply selected
      </Button>
    </Stack>
  );
};
